/**
 * Pick-location helpers shared by the warehouse Sale Order page and its print
 * view, so the screen and the printout always filter and order line items the
 * same way. Building / Aisle / Bin come from the product catalog
 * (`productBuilding` etc. on each item from GET /purchase-orders/:id).
 */

/** Filter value meaning "items with no Building (or Aisle) set". */
export const LOCATION_NOT_SET = '__none__';

export interface LocatedItem {
  productBuilding?: string | null;
  productAisle?: string | null;
  productBin?: string | null;
}

/** The line-item fields the Sale Order page and print view read. */
export interface SaleOrderLine extends LocatedItem {
  id: string;
  productName: string;
  productBarcode?: string | null;
  unitOfMeasure: string;
  quantity: string | number;
}

export interface LocationFilter {
  building: string; // '' = all
  aisle: string; // '' = all
}

const clean = (v: string | null | undefined) => (v ?? '').trim();

const matches = (value: string | null | undefined, filter: string) =>
  !filter || (filter === LOCATION_NOT_SET ? clean(value) === '' : clean(value) === filter);

/** Natural order ("A2" before "A10"), with blanks last. */
function compareLocation(a: string, b: string): number {
  if (a === b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

/** Walking order: Building → Aisle → Bin/Bay; items without a location sink to the end. */
export function sortByLocation<T extends LocatedItem>(items: T[]): T[] {
  return [...items].sort(
    (x, y) =>
      compareLocation(clean(x.productBuilding), clean(y.productBuilding)) ||
      compareLocation(clean(x.productAisle), clean(y.productAisle)) ||
      compareLocation(clean(x.productBin), clean(y.productBin)),
  );
}

export function filterByLocation<T extends LocatedItem>(items: T[], filter: LocationFilter): T[] {
  return items.filter((it) => matches(it.productBuilding, filter.building) && matches(it.productAisle, filter.aisle));
}

function options(values: string[], hasBlank: boolean, allLabel: string) {
  const unique = [...new Set(values.filter(Boolean))].sort((a, b) => compareLocation(a, b));
  return [
    { value: '', label: allLabel },
    ...unique.map((v) => ({ value: v, label: v })),
    ...(hasBlank ? [{ value: LOCATION_NOT_SET, label: 'Not set' }] : []),
  ];
}

export function buildingOptions(items: LocatedItem[]) {
  const values = items.map((it) => clean(it.productBuilding));
  return options(values, values.includes(''), 'All buildings');
}

/** Aisles present on the order, narrowed to the selected building. */
export function aisleOptions(items: LocatedItem[], building: string) {
  const values = items.filter((it) => matches(it.productBuilding, building)).map((it) => clean(it.productAisle));
  return options(values, values.includes(''), 'All aisles');
}

export function describeFilter(filter: LocationFilter): string | null {
  const label = (v: string) => (v === LOCATION_NOT_SET ? 'Not set' : v);
  const parts = [
    filter.building && `Building: ${label(filter.building)}`,
    filter.aisle && `Aisle: ${label(filter.aisle)}`,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

export function locationFilterQuery(filter: LocationFilter): string {
  const q = new URLSearchParams();
  if (filter.building) q.set('building', filter.building);
  if (filter.aisle) q.set('aisle', filter.aisle);
  const s = q.toString();
  return s ? `?${s}` : '';
}

export function readLocationFilterFromHash(): LocationFilter {
  const params = new URLSearchParams(window.location.hash.split('?')[1] ?? '');
  return { building: params.get('building') ?? '', aisle: params.get('aisle') ?? '' };
}
